import React, { useCallback } from "react";
import {
  ChatBubbleBottomCenterIcon,
  DocumentTextIcon,
  FolderIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { createBase64Image, defaultUserAvatar } from "@/helpers/images";
import { Link } from "react-router";
import { useGetUserQuery } from "@/api/users-admin";
import { useRecentItems } from "@/hooks/use-add-recent-items";
import { Item } from "@/types/fse";

type RecentItem = Item & { dateAccessed: string };

interface ItemRowProps {
  item: RecentItem;
  onItemAccess: (item: Item) => void;
}

const ItemRow: React.FC<ItemRowProps> = ({ item, onItemAccess }) => {
  const { data: owner } = useGetUserQuery(item.owner_id);
  const formattedDate = format(new Date(item.dateAccessed), "dd/MM/yyyy, HH:mm");

  const handleItemAccess = useCallback(() => {
    onItemAccess(item);
  }, [item, onItemAccess]);

  return (
    <div className="flex flex-col space-y-3 p-4 hover:bg-gray-50 sm:p-6 2xl:flex-row 2xl:items-center 2xl:justify-between 2xl:space-x-4 2xl:space-y-0">
      <div className="flex min-w-0 items-center space-x-3">
        <div className="flex-shrink-0">
          {item.type === "folder" ? (
            <FolderIcon className="h-6 w-6 text-yellow-500" />
          ) : (
            <DocumentTextIcon className="h-6 w-6 text-blue-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{item.name}</p>
          <div className="mt-1 flex items-center">
            <Avatar className="mr-2 h-5 w-5">
              <AvatarImage
                src={owner?.avatar ? createBase64Image(owner.avatar) : defaultUserAvatar}
                alt={owner?.name || "User"}
              />
              <AvatarFallback className="text-[10px]">{owner ? owner.name[0] : "U"}</AvatarFallback>
            </Avatar>
            <p className="truncate text-xs text-gray-500">
              {owner?.name || owner?.username} • Last opened: {formattedDate}
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
        <Button variant="secondary" size="sm" className="justify-center" asChild>
          <Link to={`/chat/${item.id}`} onClick={handleItemAccess}>
            <ChatBubbleBottomCenterIcon className="mr-2 h-4 w-4" />
            Q&A
          </Link>
        </Button>
        {item.parent_fs_id && (
          <Button variant="secondary" size="sm" className="justify-center" asChild>
            <Link to={`/drive/${item.parent_fs_id}`}>
              <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
              Show in My Folders
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
};

export const DriveTableRecentItems: React.FC = () => {
  const { recentItems, addRecentItem } = useRecentItems();

  const handleItemAccess = useCallback(
    (item: Item) => {
      addRecentItem(item);
    },
    [addRecentItem],
  );

  // Filter out duplicate items based on their id
  const uniqueRecentItems = recentItems.reduce((acc, current) => {
    const x = acc.find(item => item.id === current.id);
    if (!x) {
      return acc.concat([current]);
    } else {
      return acc;
    }
  }, [] as RecentItem[]);

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <ul className="divide-y divide-gray-200">
        {uniqueRecentItems.slice(0, 5).map(item => (
          <li key={item.id}>
            <ItemRow item={item} onItemAccess={handleItemAccess} />
          </li>
        ))}
      </ul>
    </div>
  );
};
